import { Icon24UtilityChevronUp, Icon24UtilityChevron } from '@care/react-icons';
import { useState } from 'react';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Box,
  Container,
  Link,
  Typography,
  Button,
} from '@mui/material';

import he from 'he';
import sanitizeHtml from 'sanitize-html';
import { cloneDeepWith } from 'lodash-es';
import { SEOSegmentationPageType, InternalLinkSectionId } from '@/__generated__/globalTypes';
import { seoSegmentationListingPage_seoSegmentationDetails_SEOSegmentationDetailsSuccess_segmentationDetails as SEOSegmentationDetailsType } from '@/__generated__/seoSegmentationListingPage';
import smartCapitalize from '@/utils/smartCapitalize';
import {
  getSEOSegmentationLinkSections_getSEOSegmentationLinkSections as getSEOSegmentationLinkSectionsType,
  getSEOSegmentationLinkSections_getSEOSegmentationLinkSections_linkList as SEOSegmentationLinkType,
} from '@/__generated__/getSEOSegmentationLinkSections';
import { usePageAttributes } from './contexts/PageAttributesContext';
import { ListContainer } from './ListContainer';

interface SeoLinkListProps {
  seoLinkList: getSEOSegmentationLinkSectionsType[];
  internalLinkingHeadline: SEOSegmentationDetailsType['internalLinkingHeadline'];
  seoSegmentNamePlural?: SEOSegmentationDetailsType['segmentNamePlural'];
}

const SeoLinkList = ({
  seoLinkList,
  internalLinkingHeadline,
  seoSegmentNamePlural,
}: SeoLinkListProps) => {
  const [showMore, setShowMore] = useState(false);
  const { segmentNameSingular, segmentNamePlural, pageType } = usePageAttributes();

  const getResourcesTabContentIndex = (url: string) => {
    const sortOrderIndex: { [key: string]: number } = {
      stateSegmentation: 1,
      cityServices: 2,
      stateServices: 3,
      costGuide: 4,
    };

    if (url.includes('/cost')) {
      return sortOrderIndex.costGuide;
    }

    if (url.includes('/all-services')) {
      const lastIndex = url.lastIndexOf('/');
      const subString = url.substring(lastIndex, url.length);

      // check if the substring is city-state or state format ('austin-tx' or 'tx')
      if (subString.includes('-')) {
        return sortOrderIndex.cityServices;
      }
      return sortOrderIndex.stateServices;
    }

    return sortOrderIndex.stateSegmentation;
  };

  const sortResourcesTabContent = (linkLists: SEOSegmentationLinkType[]) => {
    return cloneDeepWith(linkLists).sort((linkListA, linkListB) => {
      return (
        getResourcesTabContentIndex(linkListA.url) - getResourcesTabContentIndex(linkListB.url)
      );
    });
  };

  const filterSeoLinkList = (
    seoLinkSections: getSEOSegmentationLinkSectionsType[]
  ): getSEOSegmentationLinkSectionsType[] => {
    return seoLinkSections.reduce((result: any, seoLink) => {
      if (
        seoLink.id !== InternalLinkSectionId.ARTICLES &&
        !seoLink.title.toLowerCase().includes('jobs') &&
        seoLink.linkList.length > 0
      ) {
        if (seoLink.id === InternalLinkSectionId.RESOURCES) {
          const linkList = sortResourcesTabContent(seoLink.linkList);
          result.push({
            __typename: 'SEOSegmentationLinkSection',
            id: seoLink.id,
            title: seoLink.title,
            linkList,
          });

          const jobsSeoLink = seoLinkList.find((_seoLink) => _seoLink.title.includes('jobs'));
          if (jobsSeoLink) {
            jobsSeoLink.linkList.forEach((link) => {
              if (linkList.indexOf(link) === -1) {
                linkList.push(link);
              }
            });
          }
        } else {
          result.push(seoLink);
        }
      }
      return result;
    }, []);
  };

  const sortTabs = (
    seoLinkSections: getSEOSegmentationLinkSectionsType[],
    nationalOrLocalPageType: string | undefined = undefined
  ): getSEOSegmentationLinkSectionsType[] => {
    const nationalPageTabOrder: { [key: string]: number } = Object.freeze({
      RELATED_SERVICES: 1,
      TOP_CITIES: 2,
      RESOURCES: 3,
      NULL_ID: 4,
    });
    const localPageTabOrder: { [key: string]: number } = Object.freeze({
      RELATED_SERVICES: 2,
      TOP_CITIES: 1,
      RESOURCES: 3,
      NULL_ID: 4,
    });

    return cloneDeepWith(seoLinkSections).sort(
      (
        linkSectionA: getSEOSegmentationLinkSectionsType,
        linkSectionB: getSEOSegmentationLinkSectionsType
      ) => {
        const sectionAId = linkSectionA.id ? linkSectionA.id.toString() : 'NULL_ID';
        const sectionBId = linkSectionB.id ? linkSectionB.id.toString() : 'NULL_ID';
        if (nationalOrLocalPageType === SEOSegmentationPageType.NATIONAL) {
          return nationalPageTabOrder[sectionAId] - nationalPageTabOrder[sectionBId];
        }
        return localPageTabOrder[sectionAId] - localPageTabOrder[sectionBId];
      }
    );
  };

  const seoLinks = sortTabs(filterSeoLinkList(seoLinkList), pageType);

  const handleClickShow = () => {
    if (showMore) {
      setShowMore(false);
    } else {
      setShowMore(true);
    }
  };

  const tabHeadline = (title: string) => {
    if (pageType === SEOSegmentationPageType.NATIONAL) {
      if (title.includes('Help')) return `${segmentNameSingular} in Top Cities`;
      if (title.includes('Resources')) return `More Services`;
      return title;
    }

    if (title.includes('Related')) return 'Sub-categories';
    if (title.includes('Near')) {
      return seoSegmentNamePlural
        ? `${seoSegmentNamePlural} in Nearby Cities`
        : `${segmentNamePlural} in Nearby Cities`;
    }
    if (title.includes('Resources')) return `More Services`;

    return title;
  };

  const links = (linkList: SEOSegmentationLinkType[]) => {
    return linkList.map((link: SEOSegmentationLinkType, index: number) => (
      <Link
        className={!showMore && index > 19 ? 'link-hidden' : 'link-visible'}
        sx={{
          '&.link-visible': {
            cursor: 'pointer',
            lineHeight: '30px',
          },
          '&.link-hidden': {
            visibility: 'hidden',
            maxHeight: '0',
          },
        }}
        href={link.url}
        underline="none"
        key={`${link.content}-${index + 1}`}>
        <Typography
          component="span"
          variant="body2"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(he.decode(smartCapitalize(link.content))),
          }}
        />
      </Link>
    ));
  };

  const renderLinkList = (seoLink: getSEOSegmentationLinkSectionsType) => {
    return (
      <>
        <Box
          sx={{
            display: { sm: 'flex' },
          }}>
          <ListContainer>{links(seoLink.linkList).slice(0, 10)}</ListContainer>
          <ListContainer>{links(seoLink.linkList).slice(10, 20)}</ListContainer>
        </Box>
        {seoLink.linkList.length > 20 && (
          <Box
            height={!showMore ? '0px' : 'auto'}
            sx={{
              display: { sm: 'flex' },
            }}>
            <ListContainer>
              {links(seoLink.linkList).slice(
                20,
                // split links evenly between the two columns
                20 + (seoLink.linkList.length - 20) / 2
              )}
            </ListContainer>
            <ListContainer>
              {links(seoLink.linkList).slice(20 + (seoLink.linkList.length - 20) / 2)}
            </ListContainer>
          </Box>
        )}
        {!showMore && seoLink.linkList.length > 20 && (
          <Box display="flex" justifyContent="center">
            <Button onClick={handleClickShow}>
              <Typography component="span" variant="body2" color="text.primary">
                Show more
              </Typography>
              <Box ml={1} mt={0.5}>
                <Icon24UtilityChevron size={20} />
              </Box>
            </Button>
          </Box>
        )}
        {showMore && (
          <Box display="flex" justifyContent="center">
            <Button
              onClick={handleClickShow}
              sx={{
                textAlign: 'center',
                display: 'flex',
                justifyContent: 'center',
                mt: 1,
              }}>
              <Typography component="span" variant="body2">
                Show less
              </Typography>
              <Box ml={1} mt={0.5}>
                <Icon24UtilityChevronUp size={20} />
              </Box>
            </Button>
          </Box>
        )}
      </>
    );
  };

  return (
    <Grid item xs={12} md={10} style={{ margin: 'auto' }}>
      <Container disableGutters sx={{ mt: 4 }}>
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: 'h3.fontSize', sm: 'h2.fontSize' },
            color: 'default.main',
            mb: 1.5,
          }}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(he.decode(internalLinkingHeadline ?? 'Additional Services')),
          }}
        />
        <Accordion defaultExpanded elevation={0}>
          <AccordionDetails sx={{ m: 0, p: 0 }}>
            {seoLinks.map((seoLink: getSEOSegmentationLinkSectionsType, index: number) => {
              return (
                <Accordion key={`${seoLink.title}-${index + 1}`} elevation={0}>
                  <AccordionSummary
                    sx={{
                      width: 'fit-content',
                      p: 0,
                      '& .MuiIconButton-root': {
                        mt: 0.5,
                      },
                    }}
                    expandIcon={<Icon24UtilityChevron color="#172C42" />}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { xs: 'h4.fontSize', sm: 'h3.fontSize' },
                        color: 'secondary.main',
                      }}>
                      {tabHeadline(seoLink.title)}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ m: 0, p: 0 }}>{renderLinkList(seoLink)}</AccordionDetails>
                </Accordion>
              );
            })}
          </AccordionDetails>
        </Accordion>
      </Container>
    </Grid>
  );
};

export default SeoLinkList;
